import { NetworkStatus } from "@apollo/client/core";
import * as Sentry from "@sentry/nextjs";
import { useTranslation } from "next-i18next";
import React from "react";
import { Waypoint } from "react-waypoint";

import { CommentAvatarSize } from "@/consts/avatarSizes";
import {
  EmptyText,
  ErrorStateText,
} from "@/features/common/components/informationContainer";
import {
  CommentStatusEnum,
  useCommentPageQuery,
} from "@/generated/generated-hooks";

import { ParentComment } from "../CommentCore/ParentComment";
import { CommentPlaceholder } from "../CommentPlaceholder";

type CommentSectionProps = {
  postId: string;
  isPostOwner: boolean;
  canLeaveComment: boolean;
};

export const CommentSection: React.FC<CommentSectionProps> = (props) => {
  const { postId, isPostOwner, canLeaveComment } = props;
  const { t } = useTranslation("post");
  const { data, error, loading, fetchMore, variables, networkStatus } =
    useCommentPageQuery({
      ssr: false,
      skip: !postId,
      variables: {
        input: { postId },
        avatarSize: {
          maxWidth: CommentAvatarSize.MaxWidth,
          maxHeight: CommentAvatarSize.MaxHeight,
        },
      },
      notifyOnNetworkStatusChange: true,
    });

  const fetchMoreLoading = loading && networkStatus === NetworkStatus.fetchMore;

  const waypointOnEnter = async () => {
    if (!data) return;
    const { hasNext, next } = data.commentPage.paging;
    if (loading || !hasNext || !next) return;

    fetchMore({
      variables: { ...variables, paging: { ...variables.paging, next } },
    }).catch((e) => Sentry.captureException(e));
  };

  if (error) {
    return <ErrorStateText message={error.message ?? null} />;
  }

  if (!data && loading && !fetchMoreLoading) return <CommentPlaceholder />;
  if (data.commentPage.items.length === 0)
    return <EmptyText message={t("post:comment.emptyComment")} />;

  const { items } = data.commentPage;
  return (
    <div
      className="flex flex-col w-full relative"
      data-test-id="CommentSection-container"
    >
      {/* Comments */}

      <div className="flex flex-col max-w-[700px] w-full md:w-4/5">
        {items
          .filter((item) => item.commentStatus === CommentStatusEnum.Active)
          .map((item) => {
            return (
              <div className="flex" key={item._id}>
                <ParentComment
                  comment={item}
                  postId={postId}
                  isPostOwner={isPostOwner}
                  canLeaveComment={canLeaveComment}
                />
              </div>
            );
          })}
      </div>
      <Waypoint
        onEnter={waypointOnEnter}
        key={data?.commentPage?.paging?.next}
      />

      {fetchMoreLoading && <CommentPlaceholder />}
    </div>
  );
};
